import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  RefreshCw,
  Mail,
  Trash2,
  Users,
  UserPlus,
} from "lucide-react";
import { AdminEmailComposer } from "@/components/AdminEmailComposer";
import { AdminQuickOnboard } from "@/components/AdminQuickOnboard";

interface UserData {
  id: string;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  created_at: string;
  last_seen_at: string | null;
  is_admin: boolean;
  plan_name: string;
  current_balance: number;
  login_count: number;
  tool_usage_count: number;
}

type SortField = "name" | "email" | "created_at" | "last_seen_at" | "plan_name" | "current_balance";
type SortDirection = "asc" | "desc";

export default function AdminUsers() {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("last_seen_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showQuickOnboard, setShowQuickOnboard] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, display_name, phone, created_at, last_seen_at")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch user roles
      const { data: roles } = await supabase.from("user_roles").select("user_id, role");

      // Fetch user credits
      const { data: credits } = await supabase.from("user_credits").select("user_id, current_balance, plan_id");

      // Fetch plans
      const { data: plans } = await supabase.from("subscription_plans").select("id, name");

      // Fetch activity logs for login counts
      const { data: activityLogs } = await supabase
        .from("user_activity_logs")
        .select("user_id, activity_type");

      // Build user data
      const userData: UserData[] = (profiles || []).map((p) => {
        const isAdmin = roles?.some((r) => r.user_id === p.id && r.role === "admin") || false;
        const userCredit = credits?.find((c) => c.user_id === p.id);
        const plan = plans?.find((pl) => pl.id === userCredit?.plan_id);
        const userLogs = activityLogs?.filter((l) => l.user_id === p.id) || [];
        const loginCount = userLogs.filter((l) => l.activity_type === "login").length;
        const toolCount = userLogs.filter((l) => l.activity_type === "tool_usage").length;

        return {
          id: p.id,
          email: p.email || "No email",
          display_name: p.display_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unknown",
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          phone: p.phone,
          created_at: p.created_at,
          last_seen_at: p.last_seen_at,
          is_admin: isAdmin,
          plan_name: plan?.name || "Free",
          current_balance: userCredit?.current_balance ?? 0,
          login_count: loginCount,
          tool_usage_count: toolCount,
        };
      });

      setUsers(userData);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({ title: "Error loading users", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter and sort. Admin accounts ARE included in the list (rendered with
  // an ADMIN badge so they're easy to spot) — Marcus needs to manage credits
  // and settings on his own admin account through this view.
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.email.toLowerCase().includes(term) ||
          u.display_name.toLowerCase().includes(term) ||
          u.phone?.includes(term)
      );
    }

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.display_name.localeCompare(b.display_name);
          break;
        case "email":
          comparison = a.email.localeCompare(b.email);
          break;
        case "created_at":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "last_seen_at":
          const aDate = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
          const bDate = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case "plan_name":
          comparison = a.plan_name.localeCompare(b.plan_name);
          break;
        case "current_balance":
          comparison = a.current_balance - b.current_balance;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [users, searchTerm, sortField, sortDirection]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredAndSortedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredAndSortedUsers.map((u) => u.id)));
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const selectedUsersForEmail = users.filter((u) => selectedUsers.has(u.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground">Manage all registered users</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowQuickOnboard(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Quick Onboard
          </Button>
          {selectedUsers.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowEmailComposer(true)}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email ({selectedUsers.size})
              </Button>
            </>
          )}
          <Button variant="outline" onClick={fetchUsers} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_seen_at">Last Active</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="created_at">Joined Date</SelectItem>
                <SelectItem value="plan_name">Plan</SelectItem>
                <SelectItem value="current_balance">Credits</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
            >
              {sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">
                {filteredAndSortedUsers.length} Users
              </CardTitle>
            </div>
            {selectedUsers.size > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedUsers(new Set())}>
                Clear selection
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedUsers.size === filteredAndSortedUsers.length && filteredAndSortedUsers.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("name")}
                  >
                    <div className="flex items-center">
                      Name {getSortIcon("name")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("email")}
                  >
                    <div className="flex items-center">
                      Email {getSortIcon("email")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("plan_name")}
                  >
                    <div className="flex items-center">
                      Plan {getSortIcon("plan_name")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("current_balance")}
                  >
                    <div className="flex items-center">
                      Credits {getSortIcon("current_balance")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("last_seen_at")}
                  >
                    <div className="flex items-center">
                      Last Active {getSortIcon("last_seen_at")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleSort("created_at")}
                  >
                    <div className="flex items-center">
                      Joined {getSortIcon("created_at")}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading users...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredAndSortedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-muted/50">
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.has(user.id)}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                            {user.display_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <Link
                              to={`/admin/users/${user.id}`}
                              className="font-medium hover:underline"
                            >
                              {user.display_name}
                            </Link>
                            {user.is_admin && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                Admin
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.plan_name === "Free" ? "outline" : "default"}>
                          {user.plan_name}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={user.current_balance < 5 ? "text-amber-600 font-medium" : ""}>
                          {user.current_balance}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatRelativeTime(user.last_seen_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/users/${user.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Email Composer Dialog */}
      {showEmailComposer && (
        <AdminEmailComposer
          open={showEmailComposer}
          onClose={() => setShowEmailComposer(false)}
          recipients={selectedUsersForEmail.map((u) => ({
            email: u.email,
            name: u.display_name,
          }))}
        />
      )}
      {/* Quick Onboard Dialog */}
      <AdminQuickOnboard
        open={showQuickOnboard}
        onClose={() => setShowQuickOnboard(false)}
        onUserCreated={fetchUsers}
      />
    </div>
  );
}
